import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, input, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfirmDialogComponent,
  ConfirmDialogResult,
  DropdownComponent,
  DropdownItem,
  RadioButtonGroupComponent,
  RadioButtonGroupSelectOption,
  TextWithShowMoreComponent,
  TooltipModule,
} from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { Dialog } from '@angular/cdk/dialog';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { TextareaWithAIComponent } from '@libs/textarea-with-ai';
import { Store } from '@ngrx/store';
import { selectSiteSettingsPmsType } from '@libs/store';
import { PmsType } from '@ffd/api-client';
import { ActiveStatus, CriticalStatus, HealthHistoryItemVM } from '../health-history-item/health-history-item.component';
import { HealthHistoryDatePickerComponent } from '../health-history-date-picker/health-history-date-picker.component';
import { TemporaryItemPipe } from '../../pipes/temporary-item.pipe';
import { HealthHistoryTabService } from '../health-history-tab/health-history-tab.service';

export interface HealthHistoryItemEditData {
  id: string;
  value: string;
  description: string;
  note: string;
  reportedDate: DateTime;
  startedDate: DateTime;
  inactivatedDate: DateTime | null;
  criticalStatus: CriticalStatus;
  activeStatus: ActiveStatus;
  isNewItem: boolean;
}

export interface HealthHistoryItemDeleteData {
  id: string;
  description: string;
  note: string;
  reportedDate: DateTime;
  startedDate: DateTime;
  inactivatedDate: DateTime | null;
  criticalStatus: CriticalStatus;
  activeStatus: ActiveStatus;
}

@Component({
  selector: 'lib-health-history-item-edit',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TextWithShowMoreComponent,
    SvgIconComponent,
    DropdownComponent,
    RadioButtonGroupComponent,
    HealthHistoryDatePickerComponent,
    ReactiveFormsModule,
    TemporaryItemPipe,
    LottieComponent,
    TooltipModule,
    TextareaWithAIComponent,
  ],
  templateUrl: './health-history-item-edit.component.html',
  styleUrl: './health-history-item-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HealthHistoryItemEditComponent implements OnInit {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly healthHistoryTabService = inject(HealthHistoryTabService);
  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);

  public readonly healthHistoryItem = input.required<HealthHistoryItemVM>();
  public readonly dropdownOptions = input.required<DropdownItem<string>[]>();
  public readonly isEditMode = input.required<boolean>();
  public readonly isNewItem = input.required<boolean>();
  public readonly isLoading = input.required<boolean>();
  public readonly deleteHealthHistoryEnabled = input<boolean>(true);

  public cancelButtonClicked = output<void>();
  public cancelButtonClickedNewItem = output<void>();
  public deleteButtonClicked = output<HealthHistoryItemDeleteData>();
  public saveButtonClicked = output<HealthHistoryItemEditData>();

  public isVoiceTyping = signal<boolean>(false);

  private readonly pmsType = this.store.selectSignal(selectSiteSettingsPmsType);
  public readonly isOpenDental = computed(() => this.pmsType() === PmsType.OPEN_DENTAL);

  public readonly isDeleteDisabled = computed(() => {
    return this.isLoading() || !this.deleteHealthHistoryEnabled();
  });

  public criticalStatusOptions: RadioButtonGroupSelectOption<CriticalStatus>[] = [
    {
      labelTranslationKey: 'health_history_health_history_tab_item_icon_status_non_critical',
      value: 'non-critical',
      icon: 'px-16-cross',
      iconColorClass: 'text-primary-blue',
      iconDisabledColorClass: 'text-text-extra-light',
    },
    {
      labelTranslationKey: 'health_history_health_history_tab_item_icon_status_critical',
      value: 'critical',
      icon: 'px-16-cross',
      iconColorClass: 'text-states-red',
      iconDisabledColorClass: 'text-text-light',
    },
  ];

  public formGroup = new FormGroup({
    descriptionDropdown: new FormControl<string>({ value: '', disabled: true }),
    criticalStatus: new FormControl<CriticalStatus>('non-critical'),
    reportedDatePicker: new FormControl<DateTime>({ value: DateTime.now(), disabled: true }),
    startedDatePicker: new FormControl<DateTime>(DateTime.now()),
    inactivatedDatePicker: new FormControl<DateTime>(DateTime.now()),
    noteTextArea: new FormControl<string>(''),
  });

  public readonly editText = signal<boolean>(true);
  public readonly saveClicked = signal<boolean>(false);

  public readonly startedDateSignal = toSignal(this.formGroup.controls.startedDatePicker.valueChanges);

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public ngOnInit(): void {
    this.initFormValues();

    if (this.isNewItem()) {
      this.formGroup.controls.descriptionDropdown.enable();
    }

    if (this.healthHistoryItem().activeStatus === 'inactive') {
      this.formGroup.controls.criticalStatus.disable();
      this.formGroup.controls.startedDatePicker.disable();
      this.formGroup.controls.inactivatedDatePicker.disable();
    }

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.healthHistoryTabService.setFormDirtyStateChanged(this.formGroup.dirty);
    });

    this.elementRef.nativeElement.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
  }

  public onCancelClick(): void {
    if (this.formGroup.dirty) {
      this.openCancelConfirmationDialog();
    } else {
      this.cancelEditing();
    }
  }

  public closeTextarea(): void {
    this.editText.set(false);
  }

  public onDeleteClick(): void {
    const dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[480px]'],
      disableClose: true,
      data: {
        title: 'health_history_health_history_tab_item_edit_delete_modal_title',
        description: 'health_history_health_history_tab_item_edit_delete_modal_description',
        cancelLabel: 'health_history_health_history_tab_item_edit_delete_modal_cancel',
        confirmLabel: 'health_history_health_history_tab_item_edit_delete_modal_delete',
        destructive: true,
      },
    });

    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result?.confirm) {
        this.deleteButtonClicked.emit({
          id: this.healthHistoryItem().id,
          description: this.healthHistoryItem().description,
          note: this.healthHistoryItem().note,
          reportedDate: this.healthHistoryItem().reportedDate,
          startedDate: this.healthHistoryItem().startedDate,
          inactivatedDate: this.healthHistoryItem().inactivatedDate,
          criticalStatus: this.healthHistoryItem().criticalStatus,
          activeStatus: this.healthHistoryItem().activeStatus,
        });
      }
    });
  }

  public onSaveClick(): void {
    if (!!this.formGroup.controls.descriptionDropdown.value?.includes('temp-')) {
      this.saveClicked.set(true);
      return;
    }

    const values = this.formGroup.getRawValue();

    if (!values.descriptionDropdown) {
      return;
    }

    this.healthHistoryTabService.setFormDirtyStateChanged(false);
    this.saveButtonClicked.emit({
      id: this.healthHistoryItem().id,
      value: values.descriptionDropdown ?? '',
      description: this.getNameDropdownOptionLabelFromValue(values.descriptionDropdown ?? ''),
      note: values.noteTextArea ?? '',
      reportedDate: values.reportedDatePicker ?? DateTime.now(),
      startedDate: values.startedDatePicker ?? DateTime.now(),
      inactivatedDate: values.inactivatedDatePicker ?? null,
      criticalStatus: values.criticalStatus ?? 'non-critical',
      activeStatus: values.inactivatedDatePicker && values.inactivatedDatePicker <= DateTime.now() ? 'inactive' : 'active',
      isNewItem: this.isNewItem(),
    });
  }

  protected onVoiceTypingStatusChange(inProgress: boolean): void {
    this.isVoiceTyping.set(inProgress);
    this.healthHistoryTabService.setFormDirtyStateChanged(true);
  }

  private openCancelConfirmationDialog(): void {
    const dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[480px]'],
      disableClose: true,
      data: {
        title: 'health_history_health_history_tab_item_edit_cancel_modal_title',
        description: 'health_history_health_history_tab_item_edit_cancel_modal_description',
        cancelLabel: 'health_history_health_history_tab_item_edit_cancel_modal_continue',
        confirmLabel: 'health_history_health_history_tab_item_edit_cancel_modal_discard',
      },
    });

    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result?.confirm) {
        return;
      }

      this.healthHistoryTabService.setFormDirtyStateChanged(false);
      this.cancelEditing();
    });
  }

  private cancelEditing(): void {
    if (this.isNewItem()) {
      this.cancelButtonClickedNewItem.emit();
    }

    this.cancelButtonClicked.emit();
  }

  private getNameDropdownOptionLabelFromValue(value: string): string {
    const nameDropdownOptions = this.dropdownOptions();
    const option = nameDropdownOptions.find((option) => option.value === value);

    return option ? option.label : value;
  }

  private initFormValues(): void {
    this.formGroup.setValue({
      descriptionDropdown: this.healthHistoryItem().id,
      criticalStatus: this.healthHistoryItem().criticalStatus,
      reportedDatePicker: this.healthHistoryItem().reportedDate,
      startedDatePicker: this.healthHistoryItem().startedDate,
      inactivatedDatePicker: this.healthHistoryItem().inactivatedDate,
      noteTextArea: this.healthHistoryItem().note,
    });
  }
}
